import { useAppSelector } from '@hooks/app/redux';
import { inviteClient } from '@services/external/client.api';
import { Drawer } from '@ui';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { showNotification } from '../../helpers/notification-helper';
import TStore from '../../types/store';
import { TCurrentUser } from '../../types/users';
import { AddModal, ConfirmModal, SuccessModal } from './patients-modals';
import { TFormValues } from './patients-modals/add-modal';

type CompProps = {
  onAddPatient(): void;
};

type HocProps = {
  afterSubmit?: (status?: 'error' | 'success') => void;
};

type TStep = 'form' | 'confirmation' | 'status';

const HOCAddPatient = (Component: React.ComponentType<CompProps>) => {
  const AddPatient: React.FC<HocProps> = ({ afterSubmit }) => {
    const { formatMessage } = useIntl();
    const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

    const [currentStep, setCurrentStep] = useState<TStep | undefined>();

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<TFormValues>({
      name: '',
      surname: '',
      phone: '',
    });

    const currentUser: TCurrentUser | null = useSelector(
      (store: TStore) => store.users.currentUser,
    );

    const getDoctorName = () => {
      if (currentUser) {
        const {
          profile: { firstName, lastName, titleBeforeName, titleAfterName },
        } = currentUser;
        return `${titleBeforeName || ''} ${firstName} ${lastName} ${titleAfterName || ''}`.trim();
      }
      return '';
    };

    const onSuccess = () => {
      setTimeout(() => {
        setLoading(false);
        setCurrentStep('status');
        afterSubmit?.('success');
      }, 300);
    };

    const onError = (e: any) => {
      setLoading(false);
      setCurrentStep(undefined);
      afterSubmit?.('error');

      showNotification({
        type: 'ERROR',
        title: <FormattedMessage id="server_error.error_occurred" />,
        copy: <FormattedMessage id="server_error.something_wrong" />,
      });
    };

    const onAddSubmit = (values: TFormValues) => {
      setFormData(values);
      setCurrentStep('confirmation');
    };

    const onConfirm = async () => {
      setLoading(true);
      const message = formatMessage(
        { id: 'patients_confirm_modal.message' },
        {
          name: `${formData.name} ${formData.surname}`,
          doctor: getDoctorName(),
        },
      );

      try {
        await inviteClient(providerId, {
          message,
          phoneNumber: formData.phone.replace(/ /g, '').trim(),
        });

        onSuccess();
      } catch (error) {
        onError(error);
      }
    };

    return (
      <>
        <Component onAddPatient={() => setCurrentStep('form')} />
        <Drawer
          visible={!!currentStep}
          onClose={() => setCurrentStep(undefined)}
          destroyOnClose
          title={<FormattedMessage id="patients_add_modal.title" />}
        >
          {currentStep === 'form' ? <AddModal onSubmit={onAddSubmit} /> : null}
          {currentStep === 'confirmation' ? (
            <ConfirmModal
              onConfirm={onConfirm}
              fullName={`${formData.name} ${formData.surname}`}
              doctor={getDoctorName()}
              loading={loading}
            />
          ) : null}
          {currentStep === 'status' ? (
            <SuccessModal fullName={`${formData.name} ${formData.surname}`} />
          ) : null}
        </Drawer>
      </>
    );
  };

  AddPatient.displayName = 'AddPatient';

  // eslint-disable-next-line react/display-name
  return AddPatient;
};

export default HOCAddPatient;
